import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { faEye, faEyeSlash, faUser, faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API_BASE_URL } from '../utils/apiBaseUrl';

const FORGOT_PASSWORD_MESSAGE = 'If an account with that username has an email on file, a temporary password has been sent.';

const LoginPage: React.FC = () => {
  const { login, loading, isAuthenticated } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotMessage, setForgotMessage] = useState<string | null>(null);
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [forgotLoading, setForgotLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await login(username, password);
    } catch (err: any) {
      setError(err.message || 'Login failed');
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError(null);
    setForgotMessage(null);
    const name = forgotUsername.trim();
    if (!name) {
      setForgotError('Username is required');
      return;
    }
    setForgotLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ username: name }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) {
        setForgotError(data.message || 'Too many password reset attempts. Please try again later.');
        return;
      }
      if (!res.ok) {
        setForgotError(data.message || 'Could not send a temporary password. Try again later.');
        return;
      }
      setForgotMessage(data.message || FORGOT_PASSWORD_MESSAGE);
    } catch {
      setForgotError('Could not reach the server. Try again later.');
    } finally {
      setForgotLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      const target = fromPath?.pathname
        ? `${fromPath.pathname}${fromPath.search || ''}`
        : '/';
      navigate(target, { replace: true });
    }
  }, [fromPath?.pathname, fromPath?.search, isAuthenticated, navigate]);

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left panel - vibrant blue with welcome message and illustration */}
      <div className="hidden md:flex md:w-1/2 bg-lts-blue flex-col justify-center p-12 text-white">
        <div className="flex flex-col gap-12">
          <div className="space-y-4">
            <h1 className="text-4xl font-bold">Welcome Back</h1>
            <p className="text-lg text-white/90">Manage your operations with LTS Portal</p>
          </div>
          <div className="flex justify-center">
            <div className="relative w-full max-w-md">
            <div className="bg-white/95 rounded-[2rem] p-8 shadow-xl">
              <svg
                viewBox="0 0 400 240"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-auto"
                aria-hidden="true"
              >
                <path
                  d="M80 180h240v4H80v-4z"
                  fill="#E2E8F0"
                />
                <rect x="100" y="60" width="80" height="100" rx="4" fill="#0074d9" fillOpacity="0.2" />
                <rect x="100" y="65" width="70" height="4" rx="2" fill="#0074d9" />
                <rect x="100" y="75" width="50" height="4" rx="2" fill="#0074d9" fillOpacity="0.6" />
                <rect x="100" y="85" width="60" height="4" rx="2" fill="#0074d9" fillOpacity="0.6" />
                <rect x="200" y="40" width="120" height="140" rx="6" fill="white" stroke="#CBD5E1" strokeWidth="2" />
                <rect x="215" y="55" width="90" height="6" rx="2" fill="#64748B" />
                <rect x="215" y="70" width="70" height="4" rx="2" fill="#94A3B8" />
                <rect x="215" y="85" width="80" height="4" rx="2" fill="#94A3B8" />
                <rect x="215" y="100" width="60" height="4" rx="2" fill="#94A3B8" />
                <circle cx="280" cy="195" r="25" fill="#0074d9" />
                <path d="M270 195l8 8 20-20" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                <rect x="260" y="50" width="40" height="24" rx="4" fill="#0074d9" />
                <rect x="268" y="56" width="24" height="12" rx="2" fill="white" />
              </svg>
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - light gray with form */}
      <div className="flex-1 flex flex-col bg-slate-100 min-h-[100dvh] md:min-h-screen">
        {/* Mobile: show branding at top */}
        <div className="md:hidden p-6 bg-lts-blue text-white">
          <h1 className="text-xl font-bold">LTS Portal</h1>
          <p className="text-sm text-white/90 mt-1">Welcome Back</p>
        </div>

        <div className="flex-1 flex flex-col justify-center p-6 md:p-12">
          <div className="w-full max-w-md mx-auto">
            {/* Desktop: logo/brand at top of form area */}
            <div className="hidden md:block mb-8">
              <h2 className="text-2xl font-bold text-slate-800">LTS Portal</h2>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-slate-800 mb-1">
                {forgotOpen ? 'Forgot password' : 'Log In'}
              </h3>
              <p className="text-slate-500 text-sm mb-6">
                {forgotOpen
                  ? 'Enter your username. If that account has an email on file, we will send a temporary password.'
                  : 'Enter your credentials to access the portal'}
              </p>

              {forgotOpen ? (
                <>
                  {forgotError && (
                    <div className="alert alert-error text-sm mb-4">{forgotError}</div>
                  )}
                  {forgotMessage && (
                    <div className="alert alert-success text-sm mb-4">{forgotMessage}</div>
                  )}

                  <form className="flex flex-col gap-4" onSubmit={handleForgotPassword}>
                    <div className="form-control">
                      <label className="label py-1" htmlFor="forgot-username">
                        <span className="label-text font-medium text-slate-700">Username</span>
                      </label>
                      <div className="join w-full">
                        <span className="join-item bg-white flex items-center px-4 border border-slate-300 rounded-l-lg">
                          <FontAwesomeIcon icon={faUser} className="text-slate-500" aria-hidden="true" />
                        </span>
                        <input
                          id="forgot-username"
                          type="text"
                          className="input input-bordered join-item flex-1 bg-white border-slate-300 rounded-r-lg focus:border-lts-blue focus:outline-none"
                          placeholder="Enter your username"
                          value={forgotUsername}
                          onChange={(e) => setForgotUsername(e.target.value)}
                          autoComplete="username"
                          required
                        />
                      </div>
                    </div>

                    <button
                      className="btn w-full mt-4 bg-lts-blue hover:bg-lts-blue/90 text-white border-0"
                      type="submit"
                      disabled={forgotLoading}
                    >
                      {forgotLoading ? 'Sending...' : 'Email temporary password'}
                    </button>
                  </form>

                  <div className="mt-4">
                    <button
                      type="button"
                      className="btn btn-link btn-sm px-0 text-slate-600"
                      onClick={() => {
                        setForgotOpen(false);
                        setForgotError(null);
                        setForgotMessage(null);
                      }}
                    >
                      Back to sign in
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {error && (
                    <div className="alert alert-error text-sm mb-4">{error}</div>
                  )}

                  <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
                    <div className="form-control">
                      <label className="label py-1" htmlFor="login-username">
                        <span className="label-text font-medium text-slate-700">Username</span>
                      </label>
                      <div className="join w-full">
                        <span className="join-item bg-white flex items-center px-4 border border-slate-300 rounded-l-lg">
                          <FontAwesomeIcon icon={faUser} className="text-slate-500" aria-hidden="true" />
                        </span>
                        <input
                          id="login-username"
                          type="text"
                          className="input input-bordered join-item flex-1 bg-white border-slate-300 rounded-r-lg focus:border-lts-blue focus:outline-none"
                          placeholder="Enter your username"
                          value={username}
                          onChange={e => setUsername(e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-control">
                      <label className="label py-1" htmlFor="login-password">
                        <span className="label-text font-medium text-slate-700">Password</span>
                      </label>
                      <div className="join w-full">
                        <span className="join-item bg-white flex items-center px-4 border border-slate-300">
                          <FontAwesomeIcon icon={faLock} className="text-slate-500" aria-hidden="true" />
                        </span>
                        <input
                          id="login-password"
                          type={showPassword ? 'text' : 'password'}
                          className="input input-bordered join-item flex-1 bg-white border-slate-300 rounded-r-lg focus:border-lts-blue focus:outline-none"
                          placeholder="Enter your password"
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-ghost join-item bg-white border border-slate-300 border-l-0"
                          onClick={() => setShowPassword(v => !v)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          tabIndex={-1}
                        >
                          <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="text-slate-500" />
                        </button>
                      </div>
                    </div>

                    <button
                      className="btn w-full mt-4 bg-lts-blue hover:bg-lts-blue/90 text-white border-0"
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? 'Logging in...' : 'Log In'}
                    </button>
                  </form>

                  <div className="mt-4">
                    <button
                      type="button"
                      className="btn btn-link btn-sm px-0 text-slate-600"
                      onClick={() => {
                        setForgotOpen(true);
                        setForgotError(null);
                        setForgotMessage(null);
                        setForgotUsername((current) => current || username);
                      }}
                    >
                      Forgot password?
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
